import React from "react";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";

// ===== Admin Imports =====
import AdminLogin from "./admin/AdminLogin";
import AdminLayout from "./admin/AdminLayout";
import AdminDashboard from "./admin/AdminDashboard";
import AddCourse from "./admin/AddCourse";
import AdminCourse from "./admin/adminCourse";
import AdminCertificate from "./admin/adminCertificate";
import EditCourse from "./admin/EditCourse";
import AddCertificate from "./admin/addCertificate";
import EditCertificate from "./admin/editCertificate";
import AdminPartner from "./admin/AdminPartner";
import AdminEnroll from "./admin/adminEnroll";
import AdminEventPoster from "./admin/AdminEventPoster";
import UploadEvent from "./admin/uploadEvent";
import AdminForm from "./admin/AdminForm";
import AdminMessages from "./admin/AdminMessages";
import AdminIVForm from "./admin/admin_iv";
import AdminProduct from "./admin/adminProduct";
import AddProduct from "./admin/addProduct";
import EditProduct from "./admin/editProduct";
import ProductQuote from "./admin/adminproductQuotes";
import AdminQuizScores from "./admin/adminQuizScores";
import AdminGallery from "./admin/adminGallery";
import AdminStats from "./admin/adminStats";
import AdminTestimonials from "./admin/adminTestimonials";
import AdminQuizQuestions from "./admin/adminQuizQuestions";
import AddAdmin from "./admin/addAdmin";
import ViewAdmins from "./admin/ViewAdmins";
import EditAdmin from "./admin/EditAdmin";
import CareerApplications from "./admin/careerApplications";
import JobPostings from "./admin/jobPostings";
import FranchiseApplications from "./admin/franchiseApplications";
import AdminOffer from "./admin/AdminOffer";
import AdminConsultation from "./admin/AdminConsultation";
import AdminAbout from "./admin/AdminAbout";
import AdminLegal from "./admin/AdminLegal";

// ===== User Imports =====
import Navbar from "./components/Navbar";
import Footer from "./components/footer";
import Gallery from "./components/Gallery";
import Home from "./components/Home";
import CertificateEnrollTemplate from "./components/CertificateEnrollTemplate";
import EducationPurpose from "./components/EducationPurpose";
import Contact from "./components/Contact";
import About from "./components/About";
import Certificate from "./components/Certificate";
import CourseList from "./components/CourseList";
import CourseEnrollTemplate from "./components/courseEnrollTemplate";
import WhatsAppWidget from "./components/WhatsAppWidget";
import ScrollToTop from "./components/ScrollToTop";
import ProtectedRoute from "./components/ProtectedRoute";
import OurProducts from "./components/OurProducts";
import ProductViewTemplate from "./components/productViewTemplate";
import Quiz from "./components/quiz";
import Careers from "./components/Careers";
import Franchise from "./components/Franchise";
import PrivacyPolicy from "./components/PrivacyPolicy";
import TermsConditions from "./components/TermsAndConditions";

import "./App.css";

function App() {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith("/admin");

  React.useEffect(() => {
    if (isAdminRoute) {
      document.body.classList.add("admin-body-reset");
    } else {
      document.body.classList.remove("admin-body-reset");
    }
  }, [isAdminRoute]);

  return (
    <>
      <ScrollToTop />

      {isAdminRoute ? (
        // -----------------------
        //  ADMIN ROUTES
        // -----------------------
        <Routes>
          {/* Public login */}
          <Route path="/admin" element={<AdminLogin />} />

          <Route element={<AdminLayout />}>
            <Route path="/admin/dashboard" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
            {/* Redirect legacy panel to dashboard */}
            <Route path="/admin/panel" element={<Navigate to="/admin/dashboard" replace />} />

            {/* Permission-based routes */}
            <Route path="/admin/courses" element={<ProtectedRoute requiredPermission="courses"><AdminCourse /></ProtectedRoute>} />

          <Route
            path="/admin/addcourse"
            element={
              <ProtectedRoute requiredPermission="courses">
                <AddCourse />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/editCourse/:id"
            element={
              <ProtectedRoute requiredPermission="courses">
                <EditCourse />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/certificates"
            element={
              <ProtectedRoute requiredPermission="certificates">
                <AdminCertificate />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/add-certificate"
            element={
              <ProtectedRoute requiredPermission="certificates">
                <AddCertificate />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/editcertificate/:id"
            element={
              <ProtectedRoute requiredPermission="certificates">
                <EditCertificate />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/partners"
            element={
              <ProtectedRoute requiredPermission="partners">
                <AdminPartner />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/enroll"
            element={
              <ProtectedRoute requiredPermission="enroll_form">
                <AdminEnroll />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/event-poster"
            element={
              <ProtectedRoute requiredPermission="event_poster">
                <AdminEventPoster />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/userEvent"
            element={
              <ProtectedRoute requiredPermission="user_event">
                <UploadEvent />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/adminform"
            element={
              <ProtectedRoute requiredPermission="being_partner">
                <AdminForm />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/messages"
            element={
              <ProtectedRoute requiredPermission="get_in_touch_messages">
                <AdminMessages />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/ivform"
            element={
              <ProtectedRoute requiredPermission="iv_forms">
                <AdminIVForm />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/products"
            element={
              <ProtectedRoute requiredPermission="product_details">
                <AdminProduct />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/add-product"
            element={
              <ProtectedRoute requiredPermission="product_details">
                <AddProduct />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/editProduct/:id"
            element={
              <ProtectedRoute requiredPermission="product_details">
                <EditProduct />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/product-quotes"
            element={
              <ProtectedRoute requiredPermission="product_quotes">
                <ProductQuote />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/quiz"
            element={
              <ProtectedRoute requiredPermission="quiz_scores">
                <AdminQuizScores />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/quiz-questions"
            element={
              <ProtectedRoute requiredPermission="quiz_questions">
                <AdminQuizQuestions />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/gallery"
            element={
              <ProtectedRoute requiredPermission="gallery">
                <AdminGallery />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/stats"
            element={
              <ProtectedRoute requiredPermission="stats">
                <AdminStats />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/reviews"
            element={
              <ProtectedRoute requiredPermission="reviews">
                <AdminTestimonials />
              </ProtectedRoute>
            }
          />

          {/* Add Admin — only users with admin.manage permission */}
          <Route
            path="/admin/add-admin"
            element={
              <ProtectedRoute requiredPermission="admin.manage">
                <AddAdmin />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/view-admins"
            element={
              <ProtectedRoute requiredPermission="admin-manage">
                <ViewAdmins />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/edit-admin/:id"
            element={
              <ProtectedRoute requiredPermission="admin.manage">
                <EditAdmin />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/careers"
            element={
              <ProtectedRoute requiredPermission="career_applications">
                <CareerApplications />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/job-postings"
            element={
              <ProtectedRoute requiredPermission="job_postings">
                <JobPostings />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/franchises"
            element={
              <ProtectedRoute requiredPermission="franchise_applications">
                <FranchiseApplications />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/offers"
            element={
              <ProtectedRoute requiredPermission="offers">  
                <AdminOffer />
              </ProtectedRoute>  
            }
          />

          <Route
            path="/admin/consultations"
            element={
              <ProtectedRoute requiredPermission="consultations">
                <AdminConsultation />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/about-content"
            element={
              <ProtectedRoute requiredPermission="about_content">
                <AdminAbout />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/legal-content"
            element={
              <ProtectedRoute requiredPermission="legal_content">
                <AdminLegal />
              </ProtectedRoute>
            }
          />
          </Route>
          <Route path="*" element={<h2>404 Not Found (Admin)</h2>} />
        </Routes>
      ) : (
        // -----------------------
        // USER ROUTES
        // -----------------------
        <>
          <Navbar />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/course" element={<CourseList />} />
            <Route
              path="/course-enroll-template/:id"
              element={<CourseEnrollTemplate />}
            />
            <Route
              path="/product-quote-template/:id"
              element={<ProductViewTemplate />}
            />
            <Route path="/certificate" element={<Certificate />} />
            <Route
              path="/certificate-enroll-template/:id"
              element={<CertificateEnrollTemplate />}
            />
            <Route path="/education" element={<EducationPurpose />} />
            <Route path="/quiz" element={<Quiz />} />
            <Route path="/sales" element={<OurProducts />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/franchise" element={<Franchise />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-and-conditions" element={<TermsConditions />} />
            <Route path="*" element={<h2>404 Not Found</h2>} />
          </Routes>
          <WhatsAppWidget />
          <Footer />
        </>
      )}
    </>
  );
}

export default App;
